//src/helpers/hooks.ts
import { useCallback, useRef, useContext, useEffect, useState } from "react";
import { ReportGridConfig, ReportFilterConfig, ReportGridColumn } from "app-types";
import { AppStateContext } from "../app-state";
import { createConfigService, useCustomizations, logger } from "@personicom/customizations";
import type { LogCategory } from '@personicom/customizations';
import { orderBy } from "lodash";
import { normalizeReportingConfig, isV3Config } from "./config-adapter";

export interface ReportConfigDiagnostic {
  /** Optional name of the column that triggered this diagnostic */
  field?: string;
  /** Human friendly message to surface in the UI */
  message: string;
  /** Severity flag so callers can decide how to render the issue */
  severity: "warning" | "error";
  /** Extra context that is useful when troubleshooting */
  details?: Record<string, unknown>;
}

export type ValidatedReportGridConfig = ReportGridConfig & {
  diagnostics?: ReportConfigDiagnostic[];
};

const REQUIRED_COLUMN_PROPS = ["field"] as const;

/**
 * Scores a column definition so richer V4 columns outrank legacy V3 overrides.
 */
const scoreColumn = (column: ReportGridColumn): number => {
  let score = 0;
  if (column.valueTemplate) score += 4;
  if (column.renderType) score += 3;
  if (column.formatter) score += 2;
  if (column.formatterArgs) score += 1;
  if (column.type) score += 1;
  if (column.width) score += 1;
  if (column.hide !== undefined) score += 1;
  return score;
};

/**
 * Normalizes and validates the merged column definitions, dropping invalid entries and
 * recording diagnostics so the UI can surface friendly warnings instead of crashing.
 */
export const validateColumns = (columns: ReportGridColumn[] = []): { sanitized: ReportGridColumn[]; diagnostics: ReportConfigDiagnostic[] } => {
  const sanitized: ReportGridColumn[] = [];
  const diagnostics: ReportConfigDiagnostic[] = [];
  const columnMeta = new Map<string, { index: number; score: number }>();

  columns.forEach((column, sourceIndex) => {
    const trimmedField = typeof column.field === "string" ? column.field.trim() : "";

    const missingRequired = REQUIRED_COLUMN_PROPS.filter((prop) => {
      const value = (column as unknown as Record<string, unknown>)[prop];
      return typeof value !== "string" || value.trim().length === 0;
    });

    if (missingRequired.length > 0) {
      diagnostics.push({
        severity: "error",
        field: trimmedField || undefined,
        message: `Skipped column entry ${sourceIndex + 1} because it is missing required property/properties: ${missingRequired.join(", ")}.`,
        details: { sourceIndex },
      });
      logger.warn("Reporting Hook - Dropped invalid column entry", { column, missingRequired, sourceIndex }, "Config" as LogCategory);
      return;
    }

    const normalizedColumn: ReportGridColumn = {
      ...column,
      field: trimmedField,
      // Default headerName to field if not provided
      headerName: column.headerName || trimmedField,
    };

    const candidateScore = scoreColumn(normalizedColumn);
    const existing = columnMeta.get(trimmedField);

    if (!existing) {
      columnMeta.set(trimmedField, { index: sanitized.length, score: candidateScore });
      sanitized.push(normalizedColumn);
      return;
    }

    if (candidateScore > existing.score) {
      diagnostics.push({
        severity: "warning",
        field: trimmedField,
        message: `Replaced a duplicate definition for "${trimmedField}" with the richer configuration from a later merge.`,
        details: { sourceIndex, replacedScore: existing.score, newScore: candidateScore },
      });
      logger.verbose("Reporting Hook - Replaced duplicate column with higher fidelity version", { field: trimmedField, sourceIndex, candidateScore }, "Config" as LogCategory);
      sanitized[existing.index] = normalizedColumn;
      columnMeta.set(trimmedField, { index: existing.index, score: candidateScore });
      return;
    }

    diagnostics.push({
      severity: "warning",
      field: trimmedField,
      message: `Ignored duplicate definition for "${trimmedField}" from later merge entries. Convert overrides to reporting-v2.json to avoid this warning.`,
      details: { sourceIndex, existingScore: existing.score, droppedScore: candidateScore },
    });
    logger.verbose("Reporting Hook - Ignored duplicate column entry", { field: trimmedField, sourceIndex }, "Config" as LogCategory);
  });

  return { sanitized, diagnostics };
};

export function useAppState() {
  const appState = useContext(AppStateContext);
  return appState;
}

//----
// Loads the reporting configuration from blob storage.  Gets both the
// brand-level and the subdomain-level config, and merges them together.
export function useReportConfig(){
  const { client, subdomain, assetEnvironment } = useCustomizations();
  const [config, setConfig] = useState<ValidatedReportGridConfig | undefined>(undefined);

  async function loadConfig(){
    if (!client || !assetEnvironment) {
      return;
    }

    logger.verbose("Reporting Hook - Loading reporting config for", { client, subdomain, assetEnvironment }, 'Config');
    try {
      const configService = await createConfigService({
        client,
        subdomain,
        environment: assetEnvironment as any,
      });
      const reportingConfig = await configService.fetchReportingConfig();

      // Normalize config for backward compatibility with V3 format
      const normalizedConfig = normalizeReportingConfig(reportingConfig);

      // Log format version for debugging
      if (isV3Config(reportingConfig)) {
        logger.info("Reporting Hook - Loaded V3 config format, normalized to V4", undefined, 'Config');
      }

      logger.verbose("Reporting Hook - Got merged config columns", normalizedConfig?.columns, 'Config');

      const { sanitized, diagnostics } = validateColumns(normalizedConfig.columns ?? []);
      let orderedColumns = sanitized;
      if (orderedColumns.some((c) => c.ordinal !== undefined)) {
        logger.verbose("Reporting Hook - Sorting columns by ordinal", undefined, 'Config');
        orderedColumns = orderBy(orderedColumns, (c) => c.ordinal);
      }

      logger.verbose("Reporting Hook - Final columns", orderedColumns, 'Config');

      const validatedConfig: ValidatedReportGridConfig = {
        ...normalizedConfig,
        columns: orderedColumns as ReportGridColumn[],
        diagnostics,
      };

      setConfig(validatedConfig);
    } catch (error: unknown) {
      logger.error(
        "Reporting Hook - Failed to load reporting config",
        { client, subdomain, assetEnvironment, error },
        'Config'
      );

      const fallbackColumns: ReportGridColumn[] = [];
      const diagnostics: ReportConfigDiagnostic[] = [{
        severity: "error",
        message: "We couldn’t load the reporting configuration. Please retry or verify the theme-config assets.",
        details: {
          error: error instanceof Error ? error.message : String(error),
        },
      }];

      setConfig({
        idField: 'renderId',
        columns: fallbackColumns,
        diagnostics,
      });
    }
  }

  useEffect(() => {
    if(client && assetEnvironment) loadConfig();
  }, [client, subdomain, assetEnvironment]);

  return config; 
}

//---
// Hook to call a function after a specificed timeframe.
export type UseTimeoutFnReturn = [() => boolean | null, () => void, () => void];

export default function useTimeoutFn(fn: Function, ms: number = 0): UseTimeoutFnReturn {
  const ready = useRef<boolean | null>(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const callback = useRef(fn);

  const isReady = useCallback(() => ready.current, []);

  const set = useCallback(() => {
    ready.current = false;
    timeout.current && clearTimeout(timeout.current);

    timeout.current = setTimeout(() => {
      ready.current = true;
      callback.current();
    }, ms);
  }, [ms]);

  const clear = useCallback(() => {
    ready.current = null;
    timeout.current && clearTimeout(timeout.current);
  }, []);

  // update ref when function changes
  useEffect(() => {
    callback.current = fn;
  }, [fn]);

  // set on mount, clear on unmount
  useEffect(() => {
    set();

    return clear;
  }, [ms]);

  return [isReady, clear, set];
}